// ─────────────────────────────────────────────────────────────────────────────
// AI-AUDIT: values produced by this module (from, to, waypoints — especially
// their `name` fields) are USER-CONTROLLED via URL query params and flow into
// the DOM. Any renderer consuming these values MUST use textContent / React
// children (which auto-escape), never innerHTML / dangerouslySetInnerHTML.
// See docs/AI_BLIND_SPOTS.md §1 (XSS via template interpolation). Codex
// review 2026-04-18 Critical #1 found a share-URL-delivered XSS here.
// ─────────────────────────────────────────────────────────────────────────────

import { HEIGHT_MAX_M, HEIGHT_MIN_M } from './clearance'
import { MAX_WAYPOINTS } from './constants'
import { GPH_MAX, GPH_MIN, IDLE_HOURS_MAX } from './generator'
import type { FuelType, VehicleId, Waypoint } from './types'
import { VEHICLES } from './vehicles'

interface RouteUrlState {
	from: Waypoint | null
	to: Waypoint | null
	waypoints: Waypoint[]
	vehicle: VehicleId
	fuelType: FuelType
	/** Custom MPG override — null means use the vehicle preset's baseMpg */
	customMpg: number | null
	/** Custom vehicle height (m) for bridge-aware routing — null = preset */
	customHeightM?: number | null
	/** Generator/reefer add-on on? */
	auxPowerOn?: boolean
	/** Custom generator/reefer burn rate (gal/hr) — null = preset */
	customGph?: number | null
	/** Parked generator/reefer hours on this route */
	auxIdleHours?: number
}

const MAX_WAYPOINT_NAME_LENGTH = 200

function cleanWaypointName(raw: string | null | undefined): string | null {
	const trimmed = raw?.trim()
	if (!trimmed) return null
	return trimmed.slice(0, MAX_WAYPOINT_NAME_LENGTH)
}

function parseCoordinate(raw: string | null | undefined, min: number, max: number): number | null {
	if (!raw) return null
	const parsed = Number.parseFloat(raw)
	if (!Number.isFinite(parsed) || parsed < min || parsed > max) return null
	return parsed
}

function waypointFromParts(
	nameRaw: string | null | undefined,
	latRaw: string | null | undefined,
	lngRaw: string | null | undefined,
): Waypoint | null {
	const name = cleanWaypointName(nameRaw)
	const lat = parseCoordinate(latRaw, -90, 90)
	const lng = parseCoordinate(lngRaw, -180, 180)
	if (!name || lat === null || lng === null) return null
	return { name, lat, lng }
}

/** A finite number inside [min, max], else null. */
function parseBounded(raw: string | null, min: number, max: number): number | null {
	if (raw === null) return null
	const parsed = Number.parseFloat(raw)
	return Number.isFinite(parsed) && parsed >= min && parsed <= max ? parsed : null
}

/** Vehicle-settings params. Each is emitted only when it differs from the default. */
function encodeSettings(params: URLSearchParams, state: RouteUrlState): void {
	if (state.customHeightM != null) params.set('h', state.customHeightM.toFixed(3))
	if (state.auxPowerOn) {
		params.set('gen', '1')
		if (state.customGph != null) params.set('gph', String(state.customGph))
		if (state.auxIdleHours) params.set('idle', String(state.auxIdleHours))
	}
}

function decodeSettings(searchParams: URLSearchParams) {
	const auxPowerOn = searchParams.get('gen') === '1'
	return {
		customHeightM: parseBounded(searchParams.get('h'), HEIGHT_MIN_M, HEIGHT_MAX_M),
		auxPowerOn,
		customGph: auxPowerOn ? parseBounded(searchParams.get('gph'), GPH_MIN, GPH_MAX) : null,
		auxIdleHours: auxPowerOn ? (parseBounded(searchParams.get('idle'), 0, IDLE_HOURS_MAX) ?? 0) : 0,
	}
}

function parseVehicleId(raw: string | null): VehicleId {
	if (!raw) return 'nightliner'
	return VEHICLES.find((vehicle) => vehicle.id === raw)?.id ?? 'nightliner'
}

/**
 * Encode route state into URL search params for shareable links.
 *
 * Format:
 *   ?from=Nashville,TN&fromLat=36.16&fromLng=-86.78
 *   &to=Denver,CO&toLat=39.74&toLng=-104.99
 *   &via=St.Louis,MO;38.63;-90.20|Salina,KS;38.84;-97.61
 *   &v=nightliner&f=diesel
 *   &h=4.115&gen=1&gph=1.1&idle=8   (optional vehicle settings)
 */
export function encodeRouteToUrl(state: RouteUrlState): string {
	const params = new URLSearchParams()

	if (state.from) {
		params.set('from', state.from.name)
		params.set('fromLat', state.from.lat.toFixed(4))
		params.set('fromLng', state.from.lng.toFixed(4))
	}

	if (state.to) {
		params.set('to', state.to.name)
		params.set('toLat', state.to.lat.toFixed(4))
		params.set('toLng', state.to.lng.toFixed(4))
	}

	if (state.waypoints.length > 0) {
		const via = state.waypoints
			.map((w) => `${w.name};${w.lat.toFixed(4)};${w.lng.toFixed(4)}`)
			.join('|')
		params.set('via', via)
	}

	params.set('v', state.vehicle)
	params.set('f', state.fuelType === 'diesel' ? 'd' : 'g')

	// Only emit `mpg` when the user has overridden the preset — keeps
	// default-case share links clean and shorter.
	if (state.customMpg !== null && Number.isFinite(state.customMpg)) {
		params.set('mpg', state.customMpg.toString())
	}

	encodeSettings(params, state)

	return params.toString()
}

/**
 * Decode route state from URL search params.
 * Returns null values for missing fields.
 */
export function decodeRouteFromUrl(searchParams: URLSearchParams): Required<RouteUrlState> {
	const fromName = searchParams.get('from')
	const fromLat = searchParams.get('fromLat')
	const fromLng = searchParams.get('fromLng')

	const toName = searchParams.get('to')
	const toLat = searchParams.get('toLat')
	const toLng = searchParams.get('toLng')

	const viaStr = searchParams.get('via')
	const vehicle = parseVehicleId(searchParams.get('v'))
	const fuelCode = searchParams.get('f')
	const fuelType: FuelType = fuelCode === 'g' ? 'gas' : 'diesel'

	const mpgStr = searchParams.get('mpg')
	let customMpg: number | null = null
	if (mpgStr) {
		const parsed = Number.parseFloat(mpgStr)
		// Only accept realistic MPG — if the URL is malformed or out of range,
		// fall back to preset rather than passing garbage into the pipeline.
		if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 50) {
			customMpg = parsed
		}
	}

	const from = waypointFromParts(fromName, fromLat, fromLng)
	const to = waypointFromParts(toName, toLat, toLng)

	const waypoints: Waypoint[] = viaStr
		? viaStr
				.split('|')
				.map((w) => {
					const parts = w.split(';')
					return waypointFromParts(parts[0], parts[1], parts[2])
				})
				.filter((waypoint): waypoint is Waypoint => waypoint !== null)
				.slice(0, MAX_WAYPOINTS)
		: []

	return { from, to, waypoints, vehicle, fuelType, customMpg, ...decodeSettings(searchParams) }
}
